import type { Holding } from "@/admin/types/products";

/**
 * Manufacturer-warranty fallback for warranty record cards.
 *
 * When a holding has no platform-issued warranty (and nothing pending),
 * the record pages used to show a bare "No warranty issued" placeholder
 * even though the product itself often carries a manufacturer-declared
 * duration (``product.manufacturer_warranty_months``, sourced from the
 * tenant's catalogue import). This helper turns that product fact into
 * a display record: tier "Manufacturer warranty", real duration, and an
 * expiry the existing card logic computes from ``active_from`` +
 * ``duration_in_months``.
 *
 * Generic - any tenant whose catalogue carries the months gets the
 * fallback; products without it keep today's placeholder. This is a
 * DISPLAY of manufacturer coverage, not an issued Warranty row: nothing
 * is written, and issued/pending warranties always take precedence.
 */
export function manufacturerWarrantyRecord(
  holding: Pick<Holding, "created_at" | "status"> & {
    product?: { manufacturer_warranty_months?: number | null } | null;
    product_category?: string | null;
  },
): {
  option_display_name: string;
  class_name: string;
  warranty_type: string;
  coverage_scope: null;
  duration_in_months: number;
  is_lifetime: boolean;
  active_from: string;
  status: string;
} | null {
  const months = holding.product?.manufacturer_warranty_months;
  if (typeof months !== "number" || !Number.isFinite(months) || months <= 0) {
    return null;
  }
  return {
    option_display_name: "Manufacturer warranty",
    class_name: holding.product_category || "Warranty",
    warranty_type: "MANUFACTURER_WARRANTY",
    coverage_scope: null,
    duration_in_months: months,
    is_lifetime: false,
    active_from: holding.created_at,
    status: holding.status || "Active",
  };
}
